import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router'
import { useChannel } from '@/context/ChannelContext'
import { useQueryClient } from '@tanstack/react-query'
import {
  CANCEL_REASON_LABELS,
  CARD_BRAND_LABELS,
  PAYMENT_STATUS_LABELS,
  cancelSubscription,
  getPaymentHistory,
  getSubscription,
  reactivateSubscription,
  startChangeCard,
  type CancelReason,
  type PaymentHistoryEntry,
  type Subscription,
  type SubscriptionState,
} from '@/api/billing'

type Filter = 'all' | 'subscription' | 'refund'

export function BillingPage() {
  const { selectedChannel, isLoading: channelLoading } = useChannel()
  const channelUUID = selectedChannel?.uuid || ''
  const navigate = useNavigate()

  const subQuery = useQuery({
    queryKey: ['subscription', channelUUID],
    queryFn: () => getSubscription(channelUUID),
    enabled: !!channelUUID,
    retry: false,
  })

  const historyQuery = useQuery({
    queryKey: ['payment-history', channelUUID],
    queryFn: () => getPaymentHistory(channelUUID, { limit: 100 }),
    enabled: !!channelUUID,
    retry: false,
  })

  const queryClient = useQueryClient()
  const [filter, setFilter] = useState<Filter>('all')
  const [cancelOpen, setCancelOpen] = useState(false)
  const [reactivateError, setReactivateError] = useState<string | null>(null)
  const [changeCardOpen, setChangeCardOpen] = useState(false)
  const [cardUpdatedToast, setCardUpdatedToast] = useState(false)
  const [toastMsg, setToastMsg] = useState<string | null>(null)

  async function handleReactivate() {
    if (!channelUUID) return
    setReactivateError(null)
    try {
      await reactivateSubscription(channelUUID)
      await queryClient.invalidateQueries({ queryKey: ['subscription', channelUUID] })
      setToastMsg('Подписка возобновлена')
      setTimeout(() => setToastMsg(null), 5000)
    } catch (e) {
      const err = e as Error & { code?: string }
      if (err.code === 'NO_PAYMENT_METHOD') {
        // Нет токена — открываем change-card flow вместо ошибки
        setChangeCardOpen(true)
        return
      }
      setReactivateError(err.message || 'Не удалось возобновить подписку')
    }
  }
  const [searchParams, setSearchParams] = useSearchParams()

  // ?card=updated → показываем toast и чистим URL
  useEffect(() => {
    if (searchParams.get('card') === 'updated') {
      setCardUpdatedToast(true)
      const t = setTimeout(() => setCardUpdatedToast(false), 5000)
      const next = new URLSearchParams(searchParams)
      next.delete('card')
      setSearchParams(next, { replace: true })
      return () => clearTimeout(t)
    }
  }, [searchParams, setSearchParams])

  if (channelLoading || subQuery.isLoading) {
    return (
      <div className="billing-content">
        <PageHeader />
        <div className="billing-loading">
          <div className="spinner-large" />
          <div>Загружаем подписку…</div>
        </div>
        <StyleBlock />
      </div>
    )
  }

  if (subQuery.isError) {
    return (
      <div className="billing-content">
        <PageHeader />
        <div className="billing-error">
          <h2>Не удалось загрузить подписку</h2>
          <p>
            {(subQuery.error as Error)?.message || 'Неизвестная ошибка'}.
            {' '}Сервис подписки скоро будет готов — мы доделываем интеграцию.
          </p>
          <button className="btn btn-ghost" onClick={() => subQuery.refetch()}>
            Попробовать ещё раз
          </button>
        </div>
        <StyleBlock />
      </div>
    )
  }

  const sub = subQuery.data!
  const payments = historyQuery.data?.payments || []
  const totalPaid = historyQuery.data?.totalPaid || 0
  const filteredPayments = payments.filter((p) => {
    if (filter === 'all') return true
    if (filter === 'refund') return p.type === 'refund'
    if (filter === 'subscription') return p.type === 'subscription' || p.type === 'trial'
    return true
  })

  return (
    <div className="billing-content">
      <PageHeader />

      {sub.state === 'none' ? (
        <NoneCard onActivate={() => navigate('/onboarding/tariff')} />
      ) : (
        <SubHero
          sub={sub}
          onCancelClick={() => setCancelOpen(true)}
          onReactivateClick={handleReactivate}
          onCardClick={() => setChangeCardOpen(true)}
        />
      )}
      {reactivateError && (
        <div className="billing-inline-error">{reactivateError}</div>
      )}

      <FeaturesCard />

      <HistoryCard
        payments={filteredPayments}
        totalPaid={totalPaid}
        filter={filter}
        onFilterChange={setFilter}
        isLoading={historyQuery.isLoading}
        isError={historyQuery.isError}
      />

      {cancelOpen && (
        <CancelSubscriptionModal
          channelUUID={channelUUID}
          accessEndsAt={sub.accessEndsAt}
          onClose={() => setCancelOpen(false)}
          onCancelled={() => {
            setCancelOpen(false)
            queryClient.invalidateQueries({ queryKey: ['subscription', channelUUID] })
            setToastMsg('Подписка отменена. Доступ — до конца оплаченного периода.')
            setTimeout(() => setToastMsg(null), 6000)
          }}
        />
      )}
      {changeCardOpen && (
        <ChangeCardModal
          channelUUID={channelUUID}
          hasCard={!!sub.card || !!sub.hasPaymentMethod}
          onClose={() => setChangeCardOpen(false)}
        />
      )}
      {(cardUpdatedToast || toastMsg) && (
        <div className="billing-toast">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 6L9 17l-5-5" />
          </svg>
          {toastMsg || 'Карта обновлена'}
        </div>
      )}

      <StyleBlock />
    </div>
  )
}

function PageHeader() {
  return (
    <div className="hero">
      <p className="hero-eyebrow">Подписка и оплата</p>
      <h1 className="hero-title">
        Тариф <em>«Профи»</em>
      </h1>
      <p className="hero-sub">
        Управляйте подпиской, картой и&nbsp;смотрите историю списаний. Все чеки — на ваш email.
      </p>
    </div>
  )
}

function SubHero({
  sub,
  onCancelClick,
  onReactivateClick,
  onCardClick,
}: {
  sub: Subscription
  onCancelClick: () => void
  onReactivateClick: () => void
  onCardClick: () => void
}) {
  const stateAttr = sub.state === 'past_due' ? 'past-due' : sub.state
  const pillText = pillFor(sub)
  const spotlight = spotlightFor(sub)
  const headline = headlineFor(sub)
  const banner = bannerFor(sub)
  const showFooter = sub.state !== 'none'

  return (
    <div className="sub-hero" data-state={stateAttr}>
      <div className="sub-band">
        <div className="sub-band-l">
          <span className="sub-pill">
            <span className="pip" />
            {pillText}
          </span>
          <h2 className="sub-plan">
            TeleGraphyx <em>Профи</em>
          </h2>
          <div className="sub-headline" dangerouslySetInnerHTML={{ __html: headline }} />
        </div>
        <div className="sub-spotlight">
          <div className="sub-spotlight-lab">{spotlight.label}</div>
          <div className="sub-spotlight-val">
            {spotlight.value}
            {spotlight.unit && <span className="sub-spotlight-unit">{spotlight.unit}</span>}
          </div>
          {spotlight.hint && (
            <div className="sub-spotlight-hint" dangerouslySetInnerHTML={{ __html: spotlight.hint }} />
          )}
        </div>
      </div>

      {showFooter && (
        <div className="sub-foot">
          <div className="sub-meta">
            {sub.card ? (
              <>
                <div className="sub-meta-item">
                  <span className="sub-meta-card-icon" />
                  <b>
                    {CARD_BRAND_LABELS[sub.card.brand] || sub.card.brand} •••• {sub.card.lastFour}
                  </b>
                </div>
                {sub.card.expiry && (
                  <div className="sub-meta-item">
                    до <b>{sub.card.expiry}</b>
                  </div>
                )}
              </>
            ) : sub.hasPaymentMethod ? (
              <div className="sub-meta-item">
                <span className="sub-meta-card-icon" />
                Карта привязана <span className="sub-meta-mute">(реквизиты недоступны)</span>
              </div>
            ) : (
              <div className="sub-meta-item sub-meta-warn">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="9" /><path d="M12 8v4M12 16v.01" />
                </svg>
                Карта не&nbsp;привязана
              </div>
            )}
            {sub.subscriptionStartedAt && (
              <div className="sub-meta-item">
                с <b>{formatDate(sub.subscriptionStartedAt)}</b>
              </div>
            )}
          </div>
          <div className="sub-actions">
            <ActionButtons sub={sub} onCancelClick={onCancelClick} onReactivateClick={onReactivateClick} onCardClick={onCardClick} />
          </div>
        </div>
      )}

      {banner && (
        <div className="sub-banner">
          <BannerIcon state={sub.state} />
          <span dangerouslySetInnerHTML={{ __html: banner }} />
        </div>
      )}
    </div>
  )
}

function ActionButtons({
  sub,
  onCancelClick,
  onReactivateClick,
  onCardClick,
}: {
  sub: Subscription
  onCancelClick: () => void
  onReactivateClick: () => void
  onCardClick: () => void
}) {
  const hasMethod = !!sub.card || !!sub.hasPaymentMethod
  if (sub.state === 'cancelled') {
    return (
      <button className="btn btn-primary" onClick={hasMethod ? onReactivateClick : onCardClick}>
        {hasMethod ? 'Возобновить подписку' : 'Возобновить с новой картой'}
      </button>
    )
  }
  if (sub.state === 'past_due') {
    return (
      <>
        <button className="btn btn-ghost" onClick={onCardClick}>
          Сменить карту
        </button>
        <button className="btn btn-link" onClick={onCancelClick}>
          Отменить
        </button>
      </>
    )
  }
  return (
    <>
      <button className="btn btn-ghost" onClick={onCardClick}>
        {hasMethod ? 'Сменить карту' : 'Привязать карту'}
      </button>
      <button className="btn btn-link" onClick={onCancelClick}>
        Отменить
      </button>
    </>
  )
}

function NoneCard({ onActivate }: { onActivate: () => void }) {
  return (
    <div className="sub-hero" data-state="trial">
      <div className="sub-band">
        <div className="sub-band-l">
          <span className="sub-pill">
            <span className="pip" />
            Подписка не активна
          </span>
          <h2 className="sub-plan">
            TeleGraphyx <em>Профи</em>
          </h2>
          <div className="sub-headline">
            Подключите Профи, чтобы запустить рекламу в Я.Директе, аналитику канала и&nbsp;супер-лендинги.
          </div>
        </div>
        <div className="sub-spotlight">
          <div className="sub-spotlight-lab">Стоимость</div>
          <div className="sub-spotlight-val">
            4&nbsp;900<span className="sub-spotlight-unit">₽/мес</span>
          </div>
          <div className="sub-spotlight-hint">Отмена в&nbsp;один&nbsp;клик</div>
        </div>
      </div>
      <div className="sub-foot">
        <div className="sub-meta">Списания не настроены</div>
        <div className="sub-actions">
          <button className="btn btn-primary" onClick={onActivate}>
            Подключить Профи
          </button>
        </div>
      </div>
    </div>
  )
}

function FeaturesCard() {
  const features = [
    'Реклама в Я.Директе',
    'Аналитика канала и ER',
    'Лендинги и супер-лендинги',
    'Привет-бот и умные кнопки',
    'Комментарии и модерация',
    'Поддержка пн–пт 09–21',
  ]
  return (
    <div className="card plan-bullets-card">
      <div className="card-head">
        <span className="card-eye">Что входит в Профи</span>
      </div>
      <ul className="plan-bullets">
        {features.map((text) => (
          <li key={text}>
            <span className="pip">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6L9 17l-5-5" />
              </svg>
            </span>
            {text}
          </li>
        ))}
      </ul>
      <div className="card-foot">
        Базовый тариф пока не продаём — есть только Профи. Деньги на рекламу в&nbsp;Я.Директе платите напрямую в&nbsp;Яндекс.
      </div>
    </div>
  )
}

function HistoryCard({
  payments,
  totalPaid,
  filter,
  onFilterChange,
  isLoading,
  isError,
}: {
  payments: PaymentHistoryEntry[]
  totalPaid: number
  filter: Filter
  onFilterChange: (f: Filter) => void
  isLoading: boolean
  isError: boolean
}) {
  return (
    <div className="hist-card">
      <div className="hist-head">
        <h3 className="hist-title">История платежей</h3>
        <div className="hist-filter">
          {(['all', 'subscription', 'refund'] as Filter[]).map((f) => (
            <button
              key={f}
              className={filter === f ? 'on' : ''}
              onClick={() => onFilterChange(f)}
            >
              {f === 'all' ? 'Все' : f === 'subscription' ? 'Подписка' : 'Возвраты'}
            </button>
          ))}
        </div>
        <div className="hist-spacer" />
      </div>

      {isLoading ? (
        <div className="hist-empty">Загружаем платежи…</div>
      ) : isError ? (
        <div className="hist-empty">Не удалось загрузить историю платежей.</div>
      ) : payments.length === 0 ? (
        <div className="hist-empty">Платежей пока нет.</div>
      ) : (
        <>
          <div className="hist-table">
            <div className="hist-row hist-row-head">
              <div>Дата</div>
              <div>Назначение</div>
              <div>Способ</div>
              <div className="hist-amount">Сумма</div>
              <div>Статус</div>
            </div>
            {payments.map((p) => (
              <PaymentRow key={p.id} payment={p} />
            ))}
          </div>
          <div className="hist-foot">
            {payments.length}&nbsp;{pluralPayments(payments.length)} · общая сумма&nbsp;
            <b>{formatRub(totalPaid)}</b>
          </div>
        </>
      )}
    </div>
  )
}

function PaymentRow({ payment }: { payment: PaymentHistoryEntry }) {
  const isRefund = payment.amount < 0 || payment.type === 'refund'
  return (
    <div className="hist-row">
      <div className="hist-date">
        <b>{formatDate(payment.date)}</b>
        <span>{formatTime(payment.date)}</span>
      </div>
      <div className="hist-what">
        <b>{payment.description}</b>
      </div>
      <div className="hist-method">
        <MethodLabel method={payment.method} />
      </div>
      <div className={`hist-amount ${isRefund ? 'dn' : ''}`}>
        {isRefund && payment.amount > 0 ? '−' : ''}
        {formatRub(payment.amount)}
      </div>
      <div>
        <span className={`hist-status ${statusClass(payment.status)}`}>
          <span className="pip" />
          {PAYMENT_STATUS_LABELS[payment.status]}
        </span>
      </div>
    </div>
  )
}

function MethodLabel({ method }: { method: PaymentHistoryEntry['method'] }) {
  if (!method) return <span className="hist-method-empty">—</span>
  if ('brand' in method) {
    return (
      <span className="hist-method-card">
        <span className="hist-method-card-icon" />
        {CARD_BRAND_LABELS[method.brand]} •••• {method.lastFour}
      </span>
    )
  }
  if (method.type === 'sbp') {
    return (
      <span className="hist-method-card">
        <span className="hist-method-card-icon sbp" />
        СБП
      </span>
    )
  }
  return <span className="hist-method-empty">—</span>
}

function CancelSubscriptionModal({
  channelUUID,
  accessEndsAt,
  onClose,
  onCancelled,
}: {
  channelUUID: string
  accessEndsAt?: string
  onClose: () => void
  onCancelled: () => void
}) {
  const [reason, setReason] = useState<CancelReason | null>(null)
  const [comment, setComment] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const reasons: CancelReason[] = ['expensive', 'unclear', 'not_profitable', 'missing_feature', 'just_trying', 'other']
  const accessDate = accessEndsAt
    ? new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long' }).format(new Date(accessEndsAt))
    : null

  async function handleSubmit() {
    if (!reason) {
      setError('Выберите причину')
      return
    }
    setError(null)
    setSubmitting(true)
    try {
      await cancelSubscription(channelUUID, reason, comment.trim() || undefined)
      onCancelled()
    } catch (e) {
      setError((e as Error).message || 'Не удалось отменить подписку')
      setSubmitting(false)
    }
  }

  return (
    <div className="modal-veil" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div className="modal-icon danger">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="9" /><path d="M12 8v4M12 16v.01" />
            </svg>
          </div>
          <div className="modal-h-meta">
            <h3 className="modal-title">Отменить подписку?</h3>
            <p className="modal-sub">
              {accessDate
                ? <>Доступ останется до <b>{accessDate}</b>, потом отключим автопродление.</>
                : <>Доступ останется до конца оплаченного периода, потом отключим автопродление.</>}
            </p>
          </div>
        </div>

        <div className="modal-body">
          <div className="modal-section-label">Помоги нам стать лучше — что не понравилось?</div>
          <div className="cancel-reasons">
            {reasons.map((r) => (
              <label key={r} className={`cancel-reason ${reason === r ? 'on' : ''}`}>
                <input
                  type="radio"
                  name="cancel-reason"
                  checked={reason === r}
                  onChange={() => setReason(r)}
                />
                <span>{CANCEL_REASON_LABELS[r]}</span>
              </label>
            ))}
          </div>
          {reason === 'other' && (
            <textarea
              className="cancel-comment"
              placeholder="Расскажите подробнее (необязательно)"
              rows={3}
              maxLength={500}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
          )}
          {error && <div className="email-error">{error}</div>}
        </div>

        <div className="modal-foot">
          <button className="btn btn-link" onClick={onClose} disabled={submitting}>Не отменять</button>
          <div className="modal-foot-spacer" />
          <button className="btn btn-primary btn-danger" onClick={handleSubmit} disabled={submitting || !reason}>
            {submitting ? 'Отменяем…' : 'Отменить подписку'}
          </button>
        </div>
      </div>
    </div>
  )
}

function ChangeCardModal({
  channelUUID,
  hasCard,
  onClose,
}: {
  channelUUID: string
  hasCard: boolean
  onClose: () => void
}) {
  const initialEmail = (() => {
    try { return sessionStorage.getItem('tgx-payment-email') || '' } catch { return '' }
  })()
  const [email, setEmail] = useState(initialEmail)
  const [emailError, setEmailError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [notReady, setNotReady] = useState(false)

  function validateEmail(value: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
  }

  async function handleSubmit() {
    setSubmitError(null)
    if (!validateEmail(email)) {
      setEmailError('Введите корректный email — он нужен для чека ЮКассы.')
      return
    }
    setEmailError(null)
    setSubmitting(true)
    try {
      try { sessionStorage.setItem('tgx-payment-email', email) } catch {}
      const res = await startChangeCard(channelUUID, email)
      if (!res.success || !res.paymentUrl) {
        throw new Error(res.error || 'Сервис оплаты вернул пустой ответ')
      }
      window.location.href = res.paymentUrl
    } catch (e) {
      const err = e as Error & { code?: string }
      if (err.code === 'NOT_IMPLEMENTED') {
        setNotReady(true)
      } else {
        setSubmitError(err.message || 'Не удалось начать привязку карты')
      }
      setSubmitting(false)
    }
  }

  return (
    <div className="modal-veil" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div className="modal-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="6" width="18" height="13" rx="2" /><path d="M3 10h18" />
            </svg>
          </div>
          <div className="modal-h-meta">
            <h3 className="modal-title">{hasCard ? 'Сменить карту' : 'Привязать карту'}</h3>
            <p className="modal-sub">
              Откроется ЮКасса. <b>Спишется 1&nbsp;₽</b> для верификации и&nbsp;сразу вернётся обратно. После этого подписка продлится с&nbsp;новой карты.
            </p>
          </div>
        </div>

        {notReady ? (
          <div className="modal-body">
            <div className="modal-note">
              Управление картой будет доступно в&nbsp;ближайшем релизе. Сейчас, если нужно срочно&nbsp;— напишите в&nbsp;поддержку.
            </div>
          </div>
        ) : (
          <div className="modal-body">
            <label className="email-label" htmlFor="cc-email">Email для чека</label>
            <input
              id="cc-email"
              type="email"
              className={`email-input ${emailError ? 'error' : ''}`}
              placeholder="vasya@example.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                if (emailError) setEmailError(null)
              }}
              disabled={submitting}
            />
            {emailError && <div className="email-error">{emailError}</div>}
            {submitError && (
              <div className="email-error">{submitError}</div>
            )}
          </div>
        )}

        <div className="modal-foot">
          {notReady ? (
            <>
              <a className="btn btn-ghost" href="https://max.ru/u/f9LHodD0cOJxyFsfUc55-mWzIyVtOnWwMlagPfClMqClINEKzphlsr_6FQY" target="_blank" rel="noopener noreferrer">
                Открыть поддержку
              </a>
              <div className="modal-foot-spacer" />
              <button className="btn btn-primary" onClick={onClose}>Понятно</button>
            </>
          ) : (
            <>
              <button className="btn btn-link" onClick={onClose} disabled={submitting}>Отмена</button>
              <div className="modal-foot-spacer" />
              <button className="btn btn-primary" onClick={handleSubmit} disabled={submitting}>
                {submitting ? 'Открываем ЮКассу…' : hasCard ? 'Сменить карту' : 'Привязать карту'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function BannerIcon({ state }: { state: SubscriptionState }) {
  if (state === 'active' || state === 'trial') {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 6L9 17l-5-5" />
      </svg>
    )
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8v4M12 16v.01" />
    </svg>
  )
}

// ────────────────── helpers ──────────────────

function pillFor(sub: Subscription): string {
  switch (sub.state) {
    case 'trial': {
      const days = sub.trialEndsAt ? daysUntil(sub.trialEndsAt) : 0
      return `Пробный период · ${days} ${pluralDays(days)}`
    }
    case 'active':
      return sub.nextChargeDate ? `Активна · обновится ${formatDateShort(sub.nextChargeDate)}` : 'Активна'
    case 'past_due': {
      const left = sub.pastDue?.attemptsRemaining
      return left != null ? `Не оплачено · ${left} ${pluralAttempts(left)}` : 'Не оплачено'
    }
    case 'cancelled':
      return sub.accessEndsAt ? `Отменена · до ${formatDateShort(sub.accessEndsAt)}` : 'Отменена'
    default:
      return 'Не активна'
  }
}

function headlineFor(sub: Subscription): string {
  switch (sub.state) {
    case 'trial':
      return 'Тестируем все возможности тарифа. <b>Можно отменить в любой момент</b> — деньги вернутся.'
    case 'active':
      return 'Спасибо, что с нами. <b>Всё включено</b>: реклама в Я.Директе, аналитика канала, лендинги и поддержка.'
    case 'past_due':
      return 'Платёж не прошёл. <b>Кампании на паузе</b> — после оплаты возобновим автоматически.'
    case 'cancelled':
      return 'Автопродление отключено. <b>Доступ работает</b> до конца оплаченного периода.'
    default:
      return ''
  }
}

interface Spotlight {
  label: string
  value: string
  unit?: string
  hint?: string
}

function spotlightFor(sub: Subscription): Spotlight {
  switch (sub.state) {
    case 'trial': {
      const days = sub.trialEndsAt ? daysUntil(sub.trialEndsAt) : 0
      const charge = sub.nextChargeAmount ?? sub.currentAmount
      return {
        label: 'Осталось триала',
        value: String(days),
        unit: pluralDays(days),
        hint: sub.nextChargeDate
          ? `Дальше — <b>${formatRub(charge)}/мес</b>, спишется <b>${formatDateShort(sub.nextChargeDate)}</b>`
          : undefined,
      }
    }
    case 'active': {
      return {
        label: 'Следующее списание',
        value: formatRub(sub.nextChargeAmount ?? sub.currentAmount),
        hint: sub.nextChargeDate ? `Дата: <b>${formatDateShort(sub.nextChargeDate)}</b>` : undefined,
      }
    }
    case 'past_due': {
      const amt = sub.pastDue?.outstandingAmount ?? sub.currentAmount
      return {
        label: 'К оплате',
        value: formatRub(amt),
        hint: sub.pastDue?.nextRetryDate
          ? `Следующая попытка <b>${formatDateShort(sub.pastDue.nextRetryDate)}</b>`
          : 'ЮКасса повторит попытку',
      }
    }
    case 'cancelled': {
      return {
        label: 'Доступ до',
        value: sub.accessEndsAt ? formatDateShort(sub.accessEndsAt) : '—',
        hint: 'После — можно подключить снова',
      }
    }
    default:
      return { label: '', value: '' }
  }
}

function bannerFor(sub: Subscription): string | null {
  switch (sub.state) {
    case 'trial':
      return sub.trialEndsAt
        ? `Если отмените до <b>${formatDateShort(sub.trialEndsAt)}</b> — спишется <b>0&nbsp;₽</b>.`
        : null
    case 'active':
      return 'Подписка активна. Спасибо!'
    case 'past_due':
      return sub.pastDue?.nextRetryDate
        ? `Следующая попытка списания — <b>${formatDateShort(sub.pastDue.nextRetryDate)}</b>. Проверьте баланс карты.`
        : 'ЮКасса повторит попытку списания.'
    case 'cancelled':
      return sub.accessEndsAt
        ? `Можно передумать до <b>${formatDateShort(sub.accessEndsAt)}</b> и возобновить подписку без потерь.`
        : null
    default:
      return null
  }
}

function statusClass(status: PaymentHistoryEntry['status']): string {
  switch (status) {
    case 'paid': return 'ok'
    case 'failed': return 'failed'
    case 'refunded': return 'refund'
  }
}

function formatDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long' }).format(new Date(iso))
  } catch {
    return iso
  }
}
function formatDateShort(iso: string): string {
  try {
    return new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'short' }).format(new Date(iso))
  } catch {
    return iso
  }
}
function formatTime(iso: string): string {
  try {
    return new Intl.DateTimeFormat('ru-RU', { hour: '2-digit', minute: '2-digit' }).format(new Date(iso))
  } catch {
    return ''
  }
}
function formatRub(amount: number): string {
  const sign = amount < 0 ? '−' : ''
  const abs = Math.abs(amount)
  return `${sign}${abs.toLocaleString('ru-RU')} ₽`
}
function daysUntil(iso: string): number {
  const diff = new Date(iso).getTime() - Date.now()
  return Math.max(0, Math.ceil(diff / (24 * 60 * 60 * 1000)))
}
function pluralDays(n: number): string {
  const mod10 = n % 10, mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return 'день'
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'дня'
  return 'дней'
}
function pluralAttempts(n: number): string {
  const mod10 = n % 10, mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return 'попытка осталась'
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'попытки осталось'
  return 'попыток осталось'
}
function pluralPayments(n: number): string {
  const mod10 = n % 10, mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return 'платёж'
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'платежа'
  return 'платежей'
}

function StyleBlock() {
  return (
    <style>{`
      .billing-content {
        max-width: 1100px;
        margin: 0 auto;
        padding: 32px var(--tg-content-pad, 32px) 80px;
        width: 100%;
        font-family: var(--tg-font-ui, Inter, sans-serif);
      }
      .hero { margin-bottom: 28px; }
      .hero-eyebrow {
        font-weight: 700; font-size: 11px; letter-spacing: 0.12em;
        text-transform: uppercase; color: var(--tg-mute);
        margin: 0 0 14px;
      }
      .hero-title {
        font-weight: 800; font-size: 44px; line-height: 1.02;
        letter-spacing: -0.028em; color: var(--tg-ink); margin: 0 0 12px;
      }
      .hero-title em {
        font-family: var(--tg-font-italic); font-style: italic; font-weight: 400;
        color: var(--tg-blue); letter-spacing: -0.01em;
      }
      .hero-sub { font-size: 16px; color: var(--tg-mute); font-weight: 500; max-width: 640px; line-height: 1.55; margin: 0; }
      .hero-sub b { color: var(--tg-ink-2); font-weight: 600; }

      .billing-loading, .billing-error {
        background: var(--tg-surface, #fff);
        border: 1px solid var(--tg-line-2);
        border-radius: 20px;
        padding: 48px 32px;
        text-align: center;
        color: var(--tg-mute);
      }
      .billing-error h2 { color: var(--tg-ink); margin: 0 0 8px; font-size: 18px; }
      .billing-error p { margin: 0 0 16px; }
      .spinner-large {
        width: 32px; height: 32px;
        border: 3px solid var(--tg-line-2);
        border-top-color: var(--tg-blue);
        border-radius: 50%;
        animation: spin 0.8s linear infinite;
        margin: 0 auto 12px;
      }
      @keyframes spin { to { transform: rotate(360deg); } }

      /* ─── SUBSCRIPTION HERO ─── */
      .sub-hero {
        background: var(--tg-surface);
        border: 1px solid var(--tg-line-2);
        border-radius: 20px;
        overflow: hidden;
        position: relative;
        margin-bottom: 20px;
      }
      .sub-hero[data-state="trial"]    { --st-color: var(--tg-blue); --st-bg: var(--tg-blue-bg); --st-text: var(--tg-blue-press); --st-glow: rgba(0,128,255,0.18); }
      .sub-hero[data-state="active"]   { --st-color: var(--tg-up); --st-bg: rgba(0,168,96,0.10); --st-text: var(--tg-up); --st-glow: rgba(0,168,96,0.16); }
      .sub-hero[data-state="past-due"] { --st-color: var(--tg-down); --st-bg: rgba(255,64,0,0.10); --st-text: var(--tg-down); --st-glow: rgba(255,64,0,0.18); }
      .sub-hero[data-state="cancelled"]{ --st-color: var(--tg-mute); --st-bg: var(--tg-surface-2); --st-text: var(--tg-mute); --st-glow: rgba(143,158,176,0.16); }
      .sub-hero::before {
        content: ""; position: absolute; inset: 0;
        background: radial-gradient(680px 320px at 90% -20%, var(--st-glow), transparent 70%);
        pointer-events: none;
      }
      .sub-band {
        position: relative;
        display: grid; grid-template-columns: 1fr auto; gap: 24px;
        padding: 28px 32px 22px; align-items: flex-start;
      }
      @media (max-width: 720px) {
        .sub-band { grid-template-columns: 1fr; }
        .sub-spotlight { text-align: left !important; min-width: 0 !important; }
      }
      .sub-band-l { min-width: 0; }
      .sub-pill {
        display: inline-flex; align-items: center; gap: 7px;
        padding: 5px 11px 5px 9px; border-radius: var(--tg-r-full);
        font-size: 11px; font-weight: 700; letter-spacing: 0.08em;
        text-transform: uppercase;
        background: var(--st-bg); color: var(--st-text);
        margin-bottom: 12px;
      }
      .sub-pill .pip {
        width: 6px; height: 6px; border-radius: 50%; background: currentColor;
        box-shadow: 0 0 0 4px color-mix(in srgb, currentColor 18%, transparent);
      }
      .sub-plan {
        font-weight: 800; font-size: 30px; line-height: 1.1;
        letter-spacing: -0.025em; color: var(--tg-ink); margin: 0 0 4px;
      }
      .sub-plan em {
        font-family: var(--tg-font-italic); font-style: italic; font-weight: 400;
        color: var(--st-text); letter-spacing: -0.01em;
      }
      .sub-headline { font-size: 14px; color: var(--tg-ink-2); max-width: 440px; line-height: 1.5; }
      .sub-headline b { color: var(--tg-ink); font-weight: 600; }

      .sub-spotlight { text-align: right; min-width: 200px; padding-top: 4px; }
      .sub-spotlight-lab {
        font-size: 11px; font-weight: 700; letter-spacing: 0.1em;
        text-transform: uppercase; color: var(--tg-mute); margin-bottom: 8px;
      }
      .sub-spotlight-val {
        font-size: 42px; font-weight: 800; line-height: 1;
        letter-spacing: -0.03em; color: var(--tg-ink);
        font-variant-numeric: tabular-nums;
        display: inline-flex; align-items: baseline; gap: 6px;
      }
      .sub-spotlight-unit { font-size: 16px; font-weight: 600; color: var(--tg-mute); }
      .sub-spotlight-hint { font-size: 12px; color: var(--tg-mute); margin-top: 8px; line-height: 1.4; }
      .sub-spotlight-hint b { color: var(--tg-ink-2); font-weight: 600; }

      .sub-foot {
        position: relative;
        display: grid; grid-template-columns: 1fr auto; gap: 24px;
        align-items: center;
        padding: 18px 24px 18px 32px;
        background: rgba(255,255,255,0.6);
        border-top: 1px solid var(--tg-line);
      }
      @media (max-width: 720px) {
        .sub-foot { grid-template-columns: 1fr; }
      }
      .sub-meta { display: flex; flex-wrap: wrap; gap: 4px 22px; font-size: 13px; color: var(--tg-mute); }
      .sub-meta-item { display: inline-flex; align-items: center; gap: 8px; }
      .sub-meta-item b { color: var(--tg-ink); font-weight: 600; font-variant-numeric: tabular-nums; }
      .sub-meta-card-icon {
        width: 22px; height: 14px; border-radius: 3px;
        background: linear-gradient(135deg, #1a1a72, #3a3a92);
        display: inline-block;
      }
      .sub-meta-mute { color: var(--tg-mute-2, #b4c0d0); font-size: 12px; margin-left: 4px; }
      .sub-meta-warn { color: var(--tg-down, #ff4000); }
      .sub-meta-warn svg { width: 14px; height: 14px; }
      .sub-actions { display: flex; gap: 8px; align-items: center; flex-shrink: 0; flex-wrap: wrap; }

      .btn {
        display: inline-flex; align-items: center; justify-content: center; gap: 7px;
        padding: 10px 18px; border-radius: var(--tg-r-md);
        font-family: inherit; font-weight: 600; font-size: 13.5px;
        border: 1px solid transparent; cursor: pointer;
        transition: all var(--tg-dur-fast, 120ms);
        white-space: nowrap; text-decoration: none;
      }
      .btn:disabled { opacity: 0.5; cursor: not-allowed; }
      .btn-primary { background: var(--tg-blue); color: white; }
      .btn-primary:hover:not(:disabled) { background: var(--tg-blue-hover); }
      .btn-primary.danger { background: var(--tg-down); }
      .btn-ghost { background: var(--tg-surface); color: var(--tg-ink-2); border-color: var(--tg-line-2); }
      .btn-ghost:hover { background: var(--tg-surface-2); border-color: var(--tg-line); color: var(--tg-ink); }
      .btn-link {
        background: transparent; border: 0; padding: 10px 6px;
        color: var(--tg-mute); font-size: 13px; font-family: inherit; font-weight: 500; cursor: pointer;
      }
      .btn-link:hover { color: var(--tg-down); }

      .sub-banner {
        position: relative;
        display: flex; align-items: center; gap: 12px;
        padding: 14px 32px;
        border-top: 1px solid var(--tg-line);
        font-size: 13.5px; line-height: 1.45;
        background: var(--st-bg); color: var(--st-text);
      }
      .sub-banner svg { width: 18px; height: 18px; flex-shrink: 0; }
      .sub-banner b { color: inherit; font-weight: 700; }

      /* ─── PLAN BULLETS ─── */
      .card {
        background: var(--tg-surface);
        border: 1px solid var(--tg-line-2);
        border-radius: var(--tg-r-lg, 14px);
        padding: 22px 24px;
        margin-bottom: 20px;
      }
      .card-head { margin-bottom: 12px; }
      .card-eye {
        font-size: 11px; font-weight: 700; letter-spacing: 0.08em;
        text-transform: uppercase; color: var(--tg-mute);
      }
      .plan-bullets {
        list-style: none; margin: 0; padding: 0;
        display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px 24px;
        font-size: 14px; color: var(--tg-ink-2);
      }
      @media (max-width: 720px) {
        .plan-bullets { grid-template-columns: 1fr; gap: 12px; }
      }
      .plan-bullets li {
        display: flex; align-items: flex-start; gap: 10px; min-width: 0; overflow-wrap: anywhere;
      }
      .plan-bullets .pip {
        width: 18px; height: 18px; border-radius: 50%;
        background: rgba(0,168,96,0.12); color: var(--tg-up);
        display: inline-flex; align-items: center; justify-content: center;
        flex-shrink: 0;
      }
      .plan-bullets .pip svg { width: 11px; height: 11px; }
      .card-foot {
        margin-top: 16px;
        padding-top: 14px;
        border-top: 1px solid var(--tg-line);
        font-size: 12px; color: var(--tg-mute); line-height: 1.5;
      }

      /* ─── HISTORY ─── */
      .hist-card {
        background: var(--tg-surface);
        border: 1px solid var(--tg-line-2);
        border-radius: var(--tg-r-lg, 14px);
        padding: 22px 0 0;
        overflow: hidden;
      }
      .hist-head {
        display: flex; align-items: center; gap: 16px;
        padding: 0 24px 16px;
        border-bottom: 1px solid var(--tg-line);
        flex-wrap: wrap;
      }
      .hist-title {
        margin: 0; font-size: 18px; font-weight: 700; color: var(--tg-ink);
        flex-shrink: 0;
      }
      .hist-filter { display: flex; gap: 4px; background: var(--tg-surface-2); padding: 3px; border-radius: var(--tg-r-md); }
      .hist-filter button {
        border: 0; background: transparent;
        padding: 6px 12px; border-radius: 7px;
        font-family: inherit; font-weight: 600; font-size: 12px;
        color: var(--tg-mute); cursor: pointer;
        transition: all var(--tg-dur-fast, 120ms);
      }
      .hist-filter button.on { background: var(--tg-surface); color: var(--tg-ink); box-shadow: 0 1px 3px rgba(0,0,0,0.06); }
      .hist-spacer { flex: 1; }

      .hist-empty {
        padding: 32px 24px;
        text-align: center;
        color: var(--tg-mute);
        font-size: 14px;
      }

      .hist-table { display: flex; flex-direction: column; }
      .hist-row {
        display: grid;
        grid-template-columns: 110px 1fr 160px 110px 110px;
        align-items: center;
        gap: 12px;
        padding: 12px 24px;
        border-bottom: 1px solid var(--tg-line);
        font-size: 13px;
      }
      .hist-row:last-child { border-bottom: 0; }
      .hist-row-head {
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: var(--tg-mute);
        padding: 10px 24px;
        background: var(--tg-surface-2);
      }
      @media (max-width: 900px) {
        .hist-row { grid-template-columns: 1fr 1fr; row-gap: 6px; }
        .hist-row-head { display: none; }
      }
      @media (max-width: 720px) {
        .hist-card { padding-top: 18px; }
        .hist-head {
          padding: 0 16px 14px;
          gap: 12px;
        }
        .hist-title { font-size: 16px; }
        .hist-filter {
          width: 100%;
          overflow-x: auto;
          scrollbar-width: none;
          box-sizing: border-box;
        }
        .hist-filter::-webkit-scrollbar { display: none; }
        .hist-filter button { flex: 0 0 auto; white-space: nowrap; }
        .hist-empty { padding: 28px 16px; font-size: 13px; }
        .hist-row { padding: 12px 16px; }
        .hist-foot { padding: 12px 16px; }
      }
      .hist-date b { display: block; color: var(--tg-ink); font-weight: 600; }
      .hist-date span { display: block; font-size: 11px; color: var(--tg-mute); margin-top: 2px; }
      .hist-what b { display: block; color: var(--tg-ink); font-weight: 600; }
      .hist-what span { display: block; font-size: 11px; color: var(--tg-mute); margin-top: 2px; }
      .hist-method-card {
        display: inline-flex; align-items: center; gap: 8px;
        color: var(--tg-ink-2); font-weight: 500;
      }
      .hist-method-card-icon {
        width: 22px; height: 14px; border-radius: 3px;
        background: linear-gradient(135deg, #1a1a72, #3a3a92);
        display: inline-block;
      }
      .hist-method-card-icon.sbp { background: linear-gradient(135deg, #ff8a00, #ff5b00); }
      .hist-method-empty { color: var(--tg-mute-2); }
      .hist-amount {
        text-align: right; font-weight: 700; color: var(--tg-ink);
        font-variant-numeric: tabular-nums;
      }
      .hist-amount.dn { color: var(--tg-down); }
      .hist-status {
        display: inline-flex; align-items: center; gap: 6px;
        padding: 4px 9px; border-radius: var(--tg-r-full, 999px);
        font-size: 11px; font-weight: 700; letter-spacing: 0.04em;
        text-transform: uppercase;
      }
      .hist-status.ok { background: rgba(0,168,96,0.12); color: var(--tg-up); }
      .hist-status.failed { background: rgba(255,64,0,0.12); color: var(--tg-down); }
      .hist-status.refund { background: var(--tg-surface-2); color: var(--tg-mute); }
      .hist-status .pip { width: 5px; height: 5px; border-radius: 50%; background: currentColor; }
      .hist-foot {
        padding: 14px 24px;
        font-size: 12px; color: var(--tg-mute);
        background: var(--tg-surface-2);
        border-top: 1px solid var(--tg-line);
      }
      .hist-foot b { color: var(--tg-ink); font-weight: 700; font-variant-numeric: tabular-nums; }

      /* ─── MODAL ─── */
      .modal-veil {
        position: fixed; inset: 0; background: rgba(38,40,45,0.5);
        backdrop-filter: blur(4px);
        display: flex; align-items: center; justify-content: center;
        z-index: 9999;
      }
      .modal {
        background: var(--tg-surface);
        border-radius: var(--tg-r-xl, 18px);
        padding: 24px; max-width: 480px; width: calc(100% - 32px);
        box-shadow: 0 24px 60px -16px rgba(38,40,45,0.3);
      }
      .modal-head { display: flex; gap: 14px; margin-bottom: 16px; }
      .modal-icon {
        width: 40px; height: 40px; border-radius: 50%;
        background: var(--tg-blue-bg); color: var(--tg-blue);
        display: flex; align-items: center; justify-content: center;
        flex-shrink: 0;
      }
      .modal-icon svg { width: 22px; height: 22px; }
      .modal-h-meta { flex: 1; min-width: 0; }
      .modal-title { margin: 0 0 4px; font-size: 18px; font-weight: 700; color: var(--tg-ink); }
      .modal-sub { margin: 0; font-size: 13px; color: var(--tg-mute); line-height: 1.5; }
      .modal-body { margin-top: 8px; display: flex; flex-direction: column; gap: 6px; }
      .modal-section-label {
        font-size: 13px;
        color: var(--tg-mute, #8f9eb0);
        margin-top: 4px;
        margin-bottom: 8px;
      }
      .modal-icon.danger {
        background: rgba(255, 64, 0, 0.12);
        color: var(--tg-down, #ff4000);
      }
      .cancel-reasons {
        display: flex;
        flex-direction: column;
        gap: 6px;
        margin-bottom: 8px;
      }
      .cancel-reason {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 10px 14px;
        background: var(--tg-surface, #fff);
        border: 1.5px solid var(--tg-line-2);
        border-radius: 10px;
        cursor: pointer;
        font-size: 13.5px;
        color: var(--tg-ink-2);
        transition: all 120ms;
      }
      .cancel-reason:hover { border-color: var(--tg-line-3); }
      .cancel-reason.on {
        border-color: var(--tg-blue, #0080ff);
        background: var(--tg-blue-bg);
        color: var(--tg-ink);
      }
      .cancel-reason input {
        accent-color: var(--tg-blue, #0080ff);
        width: 16px; height: 16px;
        cursor: pointer;
        flex-shrink: 0;
      }
      .cancel-comment {
        width: 100%;
        padding: 10px 12px;
        border: 1px solid var(--tg-line-2);
        border-radius: 8px;
        font-family: inherit;
        font-size: 13px;
        color: var(--tg-ink);
        resize: vertical;
        box-sizing: border-box;
      }
      .cancel-comment:focus { outline: 0; border-color: var(--tg-blue); }
      .btn-danger { background: var(--tg-down, #ff4000) !important; }
      .btn-danger:hover { background: #d63500 !important; }
      .billing-inline-error {
        padding: 12px 16px;
        background: rgba(255, 64, 0, 0.08);
        border: 1px solid rgba(255, 64, 0, 0.25);
        border-radius: 10px;
        color: var(--tg-down, #ff4000);
        font-size: 13px;
        margin-bottom: 16px;
      }
      .modal-foot { display: flex; gap: 8px; align-items: center; margin-top: 20px; }
      .modal-foot-spacer { flex: 1; }
      .modal-note {
        padding: 12px 14px;
        background: var(--tg-warn-bg, rgba(255, 152, 0, 0.10));
        border: 1px solid rgba(255, 152, 0, 0.3);
        border-radius: var(--tg-r-md, 10px);
        font-size: 13px;
        color: var(--tg-ink-2);
        line-height: 1.5;
      }
      .email-label {
        font-size: 11px; font-weight: 700; letter-spacing: 0.06em;
        text-transform: uppercase; color: var(--tg-mute);
        margin-bottom: 4px;
      }
      .email-input {
        padding: 11px 13px;
        border: 1.5px solid var(--tg-line-2, #f0f0f4);
        border-radius: var(--tg-r-md, 10px);
        font-family: inherit;
        font-size: 14px;
        color: var(--tg-ink, #111);
        outline: none;
        transition: border-color 120ms;
      }
      .email-input:focus { border-color: var(--tg-blue, #0080ff); }
      .email-input.error { border-color: var(--tg-down, #ff4000); }
      .email-error { font-size: 12px; color: var(--tg-down, #ff4000); }

      .billing-toast {
        position: fixed;
        bottom: 32px;
        left: 50%;
        transform: translateX(-50%);
        display: inline-flex;
        align-items: center;
        gap: 10px;
        padding: 12px 20px;
        background: var(--tg-up, #00a860);
        color: white;
        border-radius: var(--tg-r-md, 10px);
        font-weight: 600;
        font-size: 14px;
        box-shadow: 0 12px 32px -8px rgba(0, 168, 96, 0.5);
        z-index: 10000;
        animation: toast-in 200ms ease-out;
      }
      .billing-toast svg { width: 16px; height: 16px; }
      @keyframes toast-in {
        from { opacity: 0; transform: translate(-50%, 20px); }
        to { opacity: 1; transform: translate(-50%, 0); }
      }
    `}</style>
  )
}
